import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { take, catchError } from 'rxjs/operators';
import { SpecialtyModel } from '../../models';
import { SpecialtyService } from '../../services/specialty.service';
import { nrError } from '../../helpers/newrelic.helper';

/**
 * Resolves specialty data before the specialty route activates.
 * Same pattern as ProgramResolver: data is available on first paint (SSR + client).
 */
@Injectable({ providedIn: 'root' })
export class SpecialtyResolver implements Resolve<SpecialtyModel | null | UrlTree> {
  constructor(
    private specialtyService: SpecialtyService,
    private router: Router,
  ) {}

  resolve(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<SpecialtyModel | null | UrlTree> {
    const slug = route.paramMap.get('slug');
    if (!slug) return of(null);

    const normalized = this.normalizeSlug(slug);
    if (normalized !== slug) {
      const childPath = route.firstChild?.url.map((segment) => segment.path).join('/');
      const redirectCommands = childPath
        ? ['/specialty', normalized, childPath]
        : ['/specialty', normalized];
      return of(this.router.createUrlTree(redirectCommands));
    }

    return this.specialtyService.getSpecialty(`/specialty/${slug}`).pipe(
      take(1),
      catchError((err) => {
        nrError(err, { context: 'specialty_resolver', slug });
        return of(this.router.createUrlTree(['/404']));
      }),
    );
  }

  private normalizeSlug(slug: string): string {
    return slug
      .toLowerCase()
      .replace(/[(),]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
}
